import { ClipboardList, CheckCircle2, Paperclip } from 'lucide-react';
import { Card, CardHeader, CardSection } from '../ui/Card';
import { Pill } from '../ui/Pill';
import { ProgressBar } from '../ui/ProgressBar';
import { PersonFormSubmissions } from './PersonFormSubmissions';
import {
  usePersonOnboarding,
  type OnboardingChecklistItem,
} from '../../hooks/usePersonOnboarding';

/**
 * Read-only onboarding status for a person, shown on mobilisation desks.
 * The server decides what comes back: a station/reception/coordinator
 * viewer gets titles + status only ("5 of 10 done") and never the
 * captured contents. The onboarding/admin role gets the full data
 * elsewhere; this card only ever renders the checklist.
 */

interface UploadCapture {
  title: string;
  files?: { name: string; key: string; content_type: string }[];
  submitted_at?: string;
}

function statusPill(status: OnboardingChecklistItem['status']) {
  switch (status) {
    case 'complete':
      return <Pill tone="success" size="sm" icon={<CheckCircle2 className="h-3 w-3" />}>Done</Pill>;
    case 'awaiting_review':
      return <Pill tone="info" size="sm">In review</Pill>;
    case 'changes_requested':
      return <Pill tone="warning" size="sm">Changes</Pill>;
    default:
      return <Pill tone="neutral" size="sm">Outstanding</Pill>;
  }
}

export function OnboardingChecklistCard({ personId }: { personId: string }) {
  const q = usePersonOnboarding(personId);

  if (q.isLoading || !q.data) return null;
  const { total, done, items, uniform_sizes, desk_fields, captures } = q.data;
  if (total === 0) return null;
  // Forms are rendered separately (against their schema); the legacy capture
  // sections keep the simple key/value renderer.
  const formsCaptures = (captures?.forms as Record<string, unknown> | undefined) ?? null;
  const uploadCaptures = (captures?.uploads as Record<string, UploadCapture> | undefined) ?? null;
  const captureEntries = captures
    ? Object.entries(captures).filter(([k]) => k !== 'forms' && k !== 'uploads')
    : [];

  return (
    <Card>
      <CardHeader
        icon={<ClipboardList className="h-4 w-4" />}
        title="Onboarding status"
        description={`${done} of ${total} complete`}
      />
      <CardSection className="space-y-3">
        <ProgressBar value={done} total={total} ariaLabel="Onboarding completion" />
        <ul className="divide-y divide-divider/10">
          {items.map((it, i) => (
            <li key={i} className="flex items-center justify-between py-2 text-sm">
              <span className="text-fg">{it.title}</span>
              {statusPill(it.status)}
            </li>
          ))}
        </ul>
        {captureEntries.length > 0 ? (
          <div className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-wider text-fg-dim">
              Captured details (HR only)
            </div>
            {captureEntries.map(([section, value]) => (
              <div key={section} className="rounded-token-sm bg-surface-2 px-3 py-2 text-sm">
                <div className="mb-0.5 font-medium text-fg-muted">{section.replace(/_/g, ' ')}</div>
                <div className="break-words text-fg">
                  {Object.entries((value as Record<string, unknown>) ?? {})
                    .filter(([k]) => k !== 'captured_at')
                    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${String(v)}`)
                    .join(' · ')}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {formsCaptures && Object.keys(formsCaptures).length > 0 ? (
          <PersonFormSubmissions
            personId={personId}
            forms={formsCaptures as Parameters<typeof PersonFormSubmissions>[0]['forms']}
          />
        ) : null}

        {uploadCaptures && Object.keys(uploadCaptures).length > 0 ? (
          <div className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-wider text-fg-dim">
              Uploaded files (HR only)
            </div>
            {Object.values(uploadCaptures).map((u, i) => (
              <div key={i} className="rounded-token-sm bg-surface-2 px-3 py-2 text-sm">
                <div className="mb-1 font-medium text-fg-muted">{u.title}</div>
                <div className="space-y-1">
                  {(u.files ?? []).map((f) => (
                    <a
                      key={f.key}
                      href={`/api/admin/onboarding/people/${personId}/files/${f.key}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1.5 text-accent hover:underline"
                    >
                      <Paperclip className="h-3.5 w-3.5" />
                      {f.name}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {desk_fields && Object.keys(desk_fields).length > 0 ? (
          <div className="rounded-token-sm bg-surface-2 px-3 py-2 text-sm">
            <div className="mb-1 font-medium text-fg-muted">Shared details</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-fg">
              {Object.entries(desk_fields).map(([k, v]) => (
                <span key={k}>
                  <span className="text-fg-muted">{k}:</span> {String(v)}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {uniform_sizes && Object.keys(uniform_sizes).length > 0 ? (
          <div className="rounded-token-sm bg-surface-2 px-3 py-2 text-sm">
            <div className="mb-1 font-medium text-fg-muted">Uniform sizes on file</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-fg">
              {Object.entries(uniform_sizes).map(([k, v]) => (
                <span key={k}>
                  <span className="text-fg-muted">{k.replace(/_/g, ' ')}:</span> {String(v)}
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </CardSection>
    </Card>
  );
}
